import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { getState, patchState, signalStore, withComputed, withMethods, withState, WritableStateSource } from '@ngrx/signals';
import { EntityState, removeEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { NotificationType } from '../enums/NotificationId';
import { TherapyJobNotification } from '../models/TherapyJobNotification';
import { FilterNotificationPipe } from '../pipes/FilterNotifications';
import { getCriticalIndex, getCriticalType } from '../utils/getCriticalNotificationType';

import { combineLatest, map, Observable, take, tap } from 'rxjs';
import { DashboardColumn } from '../constants/defaultColumns';
import { Configuration } from '../models/Configuration';
import { SystemNotification } from '../models/Notification';
import { TherapyJobV2 } from '../models/TherapyJobV2';
import { AcknowledgeItem, ActivityLogService } from '../services/activity-log.service';
import { ConfigService, ConfigurationResponse } from '../services/config.service';
import { LocaleService } from '../services/locale.service';
import { columnNameByStatus } from '../utils/getColumnByStatus';
import { notificationsOfJob } from '../utils/notifications/jobNotifications';
import {
  buildNotification,
  checkAllNotificationConditionsPerJob,
  isWebUiNotification,
  serverLevel
} from '../utils/notifications/NotificationFunctions';
import { systemNotificationId } from '../utils/notifications/notificationId';

interface NotificationsState {
  loading: boolean;
  acknowledgments: { [key: string]: AcknowledgeItem };
}

const initialNotificationSate: NotificationsState = {
  loading: false,
  acknowledgments: {}
};

const filterNotificationPipe = new FilterNotificationPipe();

type NotificationEntities = WritableStateSource<EntityState<TherapyJobNotification>>;

/** Severity and raise time of a server notification come from the job's set, not from the web UI. */
const applyServerFields = (store: NotificationEntities, notification: SystemNotification) => {
  const id = systemNotificationId(notification);
  const current = getState(store).entityMap[id];
  const level = serverLevel(notification);
  if (!current || (current.level === level && current.timestamp === notification.since)) {
    return;
  }
  patchState(store, updateEntity({ id, changes: { level, timestamp: notification.since } }));
};

/** Adds or updates the given server notifications, one per type and job, so Dismissed flags survive. */
const renderServerNotifications = (
  store: NotificationEntities,
  notifications: Array<SystemNotification>,
  jobsById: Map<string, TherapyJobV2>,
  locale: string
) => {
  notifications.forEach((notification) => {
    const notifyAnyways = true;
    buildNotification(
      NotificationType[notification.type],
      jobsById.get(notification.therapyJobId),
      store,
      undefined,
      notifyAnyways,
      locale,
      notification
    );
    applyServerFields(store, notification);
  });
};

/**
 * Makes the shown server notifications of `therapyJobs` equal to the jobs' sets (`notificationsOfJob`): removes the
 * ones no longer in a set, adds and updates the others. Only notifications of the given jobs are touched when
 * `onlyTheseJobs`, otherwise those of jobs not given are removed too.
 */
const jobsByIdOf = (therapyJobs: Array<TherapyJobV2> | undefined): Map<string, TherapyJobV2> =>
  new Map((therapyJobs ?? []).filter((tj) => !!tj).map((tj) => [tj.id, tj]));

const syncServerNotifications = (
  store: NotificationEntities,
  therapyJobs: Array<TherapyJobV2>,
  locale: string,
  onlyTheseJobs: boolean
) => {
  const jobsById = jobsByIdOf(therapyJobs);
  const notifications = [...jobsById.values()].flatMap(notificationsOfJob);
  const activeIds = new Set(notifications.map(systemNotificationId));

  patchState(
    store,
    removeEntities(
      ({ id, type, therapyJobId }) =>
        !isWebUiNotification(type) && !activeIds.has(id) && (!onlyTheseJobs || jobsById.has(therapyJobId))
    )
  );
  renderServerNotifications(store, notifications, jobsById, locale);
};

export const NotificationsStore = signalStore(
  { providedIn: 'root' },
  withEntities<TherapyJobNotification>(),
  withDevtools('notifications'),
  withState(initialNotificationSate),
  withMethods(
    (
      store,
      config = inject(ConfigService),
      localeService = inject(LocaleService)
    ) => ({
      buildApplicationNotifications(therapyJobs: Array<TherapyJobV2>, configuration: Configuration) {
        if (!configuration) {
          console.warn('Configuration is undefined, cannot set notifications');
          return;
        }

        therapyJobs.forEach((therapyJob) => {
          checkAllNotificationConditionsPerJob(therapyJob, store, configuration, localeService.locale());
        });
      },
      /** The server notifications of every loaded job; removes those of jobs no longer loaded. */
      syncServerNotifications(therapyJobs: Array<TherapyJobV2>) {
        syncServerNotifications(store, therapyJobs ?? [], localeService.locale(), false);
      },
      /**
       * Ticker of the board and detail page: re-renders the shown pending confirmations (timeAgo, Info → Warning
       * at `escalatesAt`). Never creates or removes them, the job's set does.
       */
      refreshPendingConfirmations(therapyJobs: Array<TherapyJobV2>): void {
        const jobsById = jobsByIdOf(therapyJobs);
        const shownPendingConfirmations = [...jobsById.values()]
          .flatMap(notificationsOfJob)
          .filter((n) => NotificationType[n.type] === NotificationType.PENDING_CONFIRMATION)
          .filter((n) => !!store.entityMap()[systemNotificationId(n)]);

        renderServerNotifications(store, shownPendingConfirmations, jobsById, localeService.locale());
      },
      cleanApplicationNotifications(therapyJobIds: Array<string>) {
        if (!therapyJobIds?.length) {
          return;
        }
        patchState(
          store,
          removeEntities(({ therapyJobId }) => !therapyJobIds.includes(therapyJobId))
        );
      },
      getNotificationLevelById(therapyJobId: string): number {
        // we use the index for sorting
        return getCriticalIndex(filterNotificationPipe.transform(store.entityMap(), therapyJobId));
      },
      update(id: string, partial: Partial<TherapyJobNotification>) {
        patchState(store, updateEntity({ id, changes: partial }));
      },
      /** After a change of one job (e.g. a WebSocket event): its server notifications and the web UI ones. */
      refreshJobNotifications(therapyJob: TherapyJobV2): void {
        if (!therapyJob) {
          return;
        }
        const configuration = config.configuration()?.content;

        syncServerNotifications(store, [therapyJob], localeService.locale(), true);
        checkAllNotificationConditionsPerJob(therapyJob, store, configuration, localeService.locale());
      },
      deleteNotificationsByJobId(therapyJobId: string): void {
        patchState(
          store,
          removeEntities(
            store.entities().reduce((acc, n: TherapyJobNotification) => {
              if (n.id.endsWith(`-${therapyJobId}`)) {
                acc.push(n.id);
              }
              return acc;
            }, [])
          )
        );
      },
      setAcknowledgments(acknowledgments: Array<AcknowledgeItem>) {
        patchState(store, {
          acknowledgments: acknowledgments.reduce((map, item) => {
            map[item.therapyJobId] = item;
            return map;
          }, {})
        });
      }
    })
  ),
  withMethods(
    (
      store,
      activityLogService = inject(ActivityLogService),
      config = inject(ConfigService)
    ) => ({
      // here we request the data the notifications need besides the jobs and build them
      init(therapyJobs: Array<TherapyJobV2>): Observable<void> {
        const requests = combineLatest([
          activityLogService
            .getPendingAcknowledgement(
              therapyJobs.filter((e) => columnNameByStatus(e?.status) !== DashboardColumn.COMPLETED).map((e) => e.id)
            )
            .pipe(take(1)),
          config.getConfig().pipe(take(1))
        ]);

        return requests.pipe(
          take(1),
          tap(([pendingAck, configRes]: [Array<AcknowledgeItem>, ConfigurationResponse]) => {
            store.setAcknowledgments(pendingAck);

            const configuration = configRes.content;

            store.syncServerNotifications(therapyJobs);
            store.cleanApplicationNotifications(therapyJobs.map((tj) => tj.id));
            store.buildApplicationNotifications(therapyJobs, configuration);
          }),
          map(() => null)
        );
      },

      // here we re-use the loaded jobs to rebuild the notifications
      refreshAll(therapyJobs: Array<TherapyJobV2>) {
        const configuration = config.configuration()?.content;

        store.syncServerNotifications(therapyJobs);
        store.cleanApplicationNotifications(therapyJobs.map((tj) => tj.id));
        store.buildApplicationNotifications(therapyJobs, configuration);
      }
    })
  ),
  withComputed((state) => ({
    count: computed(() => {
      return state.entities()?.filter((notification) => notification.showAlarmCenter)?.length;
    }),
    criticality: computed(() => {
      const reduced = state.entities().filter((notification) => notification.showAlarmCenter);

      return getCriticalType(reduced);
    })
  }))
);

export type NotificationsStore = InstanceType<typeof NotificationsStore>;
